import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { useTokenList } from "@/lib/hooks/useApi";
import type { TokenListResponse } from "@/lib/types/api";
import { cn, generateTextShadow } from "@/lib/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AddTokenContainer } from "@/components/AddTokenContainer";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { Countdown } from "@/components/ui/countdown";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InfoIcon } from "lucide-react";

export const Route = createFileRoute("/(game)/top-performers")({
  component: TopPerformers,
});

const NEXT_TIMESTAMP = 1764576000; // WARN: Update This

// Helper function to transform tokenList data to top performers format
const transformToTopPerformers = (tokenList: TokenListResponse[]) => {
  return tokenList
    .sort((a, b) => b.currentPower - a.currentPower)
    .slice(0, 3)
    .map((token, index) => ({
      id: index + 1,
      rank: index + 1,
      token: token.tokenSymbol,
      score: token.currentPower,
      change: token.priceChange7d,
      category: token.position,
      player: {
        id: index + 1,
        // TokenData properties
        pythLazerId: token.pythLazerId,
        solanaPythFeedId: token.solanaPythFeedId,
        coinGeckoId: token.coinGeckoId,
        tokenName: token.tokenName,
        tokenSymbol: token.tokenSymbol,
        tokenImageLogo: token.tokenImageLogo,
        tokenTotalSupply: token.tokenTotalSupply,
        playerCard: token.playerCard,
        tokenCirculatingSupply: token.tokenCirculatingSupply,
        marketCap: token.marketCap,
        position: token.position,
        priceChange24h: token.priceChange24h,
        priceChange7d: token.priceChange7d,
        multiplier: token.multiplier,
        currentPower: token.currentPower,
        lastPower: token.lastPower,
        allocation: 20, // Default allocation
      },
    }));
};

function TopPerformers() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const { data: tokenData, isLoading, error } = useTokenList();

  // Transform tokenList data to top performers format
  const topPerformers = tokenData ? transformToTopPerformers(tokenData) : [];

  // Filter tokens based on selected filter and use their global rank
  const filteredTokens = tokenData
    ? tokenData
        .filter(
          (token) =>
            selectedFilter === "ALL" || token.position === selectedFilter,
        )
        .map((token) => {
          // Find the global rank of this token in the original sorted data
          const globalRank =
            tokenData.findIndex((t) => t.tokenSymbol === token.tokenSymbol) + 1;

          return {
            id: token.tokenSymbol,
            rank: globalRank, // Use the global rank from the original data
            name: token.tokenName,
            symbol: token.tokenSymbol,
            avatar: token.playerCard,
            playerCard: token.playerCard,
            logoUrl: token.tokenImageLogo,
            position: token.position,
            priceChange7d: token.priceChange7d,
            currentPower: token.currentPower,
            lastPower: token.lastPower,
          };
        })
    : [];

  // Trophy icons (image-based)
  const trophyIcons = [
    <img
      key="gold"
      src="https://cdn.navalabs.io/assets/gold-trophy_JGcXBt.png"
      alt="gold trophy"
      className="h-12 w-12"
    />,
    <img
      key="silver"
      src="https://cdn.navalabs.io/assets/silver-trophy_YRrXAm.png"
      alt="silver trophy"
      className="h-8 w-8"
    />,
    <img
      key="bronze"
      src="https://cdn.navalabs.io/assets/bronze-trophy_WWzMwV.png"
      alt="bronze trophy"
      className="h-8 w-8"
    />,
  ];

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center">
        <div className="text-red-500">Error loading data: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col flex-1 bg-[#1D2A52] p-4">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="relative flex flex-1 flex-col rounded-xl z-0 gap-2 overflow-hidden">
        <div className="relative flex flex-col items-center py-2 gap-1">
          <div className="flex items-center gap-2">
            <h2
              className="text-xl uppercase"
              style={{
                textShadow: generateTextShadow(1.5),
              }}
            >
              Top Token Boost
            </h2>
          </div>
          <div className="text-zinc-300 text-sm text-center">
            <div className="flex items-center justify-center gap-1 px-4">
              <span>
                Top token boost by 7-day price change, this affects in-game
                token performance
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="hover:opacity-80 transition-opacity inline-flex"
                  >
                    <InfoIcon className="w-4 h-4 text-zinc-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-sm text-left">
                  <p className="uppercase">Long:</p>
                  <ul className="flex flex-col gap-1 list-disc pl-4">
                    <li>+1% move of a token with 80 Boost counts as +1.8%</li>
                    <li>-1% move of a token with 80 Boost counts as -0.2%</li>
                  </ul>
                  <p className="uppercase mt-2">Short:</p>
                  <ul className="flex flex-col gap-1 list-disc pl-4">
                    <li>-1% move of a token with 80 Boost counts as +1.8%</li>
                    <li>+1% move of a token with 80 Boost counts as -0.2%</li>
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
            <div className="mt-1">
              Next refresh in:{" "}
              <Countdown
                startTimestamp={NEXT_TIMESTAMP}
                className="text-sky-400 font-bold"
                format="daysHoursSeconds"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-1 mt-2 min-h-0">
          {/* Filter Bar */}
          <div className="flex items-center gap-2">
            <div className="text-zinc-400 text-xs font-medium">
              Filter token by:
            </div>
            <div className="flex gap-2">
              {["ALL", "CB", "MF", "ST"].map((filter) => {
                const isSelected = selectedFilter === filter;

                return (
                  <button
                    key={filter}
                    onClick={() => setSelectedFilter(filter)}
                    className={cn(
                      "flex items-center gap-1 rounded-md px-2 py-1 hover:cursor-pointer transition-colors border",
                      "bg-[#001F18]",
                      isSelected ? "border-emerald-400" : "border-emerald-950",
                    )}
                  >
                    <span
                      className={cn(
                        "text-xs font-medium",
                        isSelected ? "text-emerald-400" : "text-zinc-400",
                      )}
                    >
                      {filter}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Token List */}
          <ScrollArea className="h-full mt-2" type="scroll">
            <div className="relative h-full">
              <div className={"absolute inset-0 flex flex-col gap-2"}>
                {filteredTokens.length > 0 ? (
                  filteredTokens.map((token) => (
                    <div
                      key={token.id}
                      className="input-field-container-light rounded-xl p-2 flex items-center gap-2"
                    >
                      {/* Rank with Trophy */}
                      <div className="flex flex-col items-center">
                        <p className="text-white font-black text-xs mt-1">
                          #{token.rank}
                        </p>
                      </div>

                      {/* Avatar */}
                      <AddTokenContainer
                        value={{
                          power: token.currentPower,
                          tokenSymbol: token.symbol,
                          tokenImageLogo: token.logoUrl,
                          playerCard: token.playerCard,
                        }}
                        positionLabel={token.position}
                        className="w-16 h-auto aspect-square"
                        tokenSymbolSize="text-sm"
                      />

                      {/* Token Info */}
                      <div className="flex-1">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-black text-md uppercase">
                              {token.name}
                            </h3>
                            <span
                              className={cn(
                                "text-xs font-bold px-2 py-0.5 rounded-sm",
                                token.position === "CB" &&
                                  "bg-yellow-950 text-yellow-400",
                                token.position === "ST" &&
                                  "bg-red-950 text-red-400",
                                token.position === "MF" &&
                                  "bg-sky-950 text-sky-400",
                              )}
                            >
                              {token.position}
                            </span>
                          </div>
                          {/* <div className="flex items-center gap-2 mb-1">
                            <span
                              className={cn(
                                "font-black text-sm",
                                token.priceChange7d >= 0
                                  ? "text-emerald-400"
                                  : "text-red-400",
                              )}
                            >
                              {token.priceChange7d.toFixed(2)}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-4 h-4"
                              viewBox="0 0 15 14"
                              fill="none"
                            >
                              <path
                                d="M7.37155 9.72366V8.73094H10.9786V10.3727L9.08374 14H8.10209L7.97272 13.7709L9.88277 10.1437V9.72366H7.37155Z"
                                fill="#9F9FA9"
                              />
                              <path
                                d="M11.5452 12.7629V11.2051C11.5452 10.3804 11.9561 9.96802 12.778 9.96802H13.9499V8.34912H15V14H12.778C11.9561 14 11.5452 13.5876 11.5452 12.7629ZM12.5953 12.8393C12.5953 13.0022 12.6765 13.0836 12.8388 13.0836H13.9499V10.8844H12.8388C12.6765 10.8844 12.5953 10.9658 12.5953 11.1287V12.8393Z"
                                fill="#9F9FA9"
                              />
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.97561 0C10.7699 0 13.8565 3.03984 13.949 6.82525C13.9576 7.17662 13.6808 7.46842 13.3307 7.47704C12.9805 7.48566 12.6897 7.20783 12.6811 6.8565C12.6054 3.7599 10.0799 1.27272 6.97561 1.27272C3.82353 1.27272 1.26829 3.8369 1.26829 6.99996C1.26829 9.26598 2.57965 11.2258 4.48522 12.1548C4.80024 12.3084 4.93154 12.6892 4.77852 13.0053C4.62547 13.3214 4.24602 13.4532 3.93101 13.2996C1.60533 12.1658 0 9.77163 0 6.99996C0 3.13399 3.12308 0 6.97561 0ZM3.99084 9.09543C3.74318 9.34393 3.74318 9.74687 3.99084 9.99537C4.23848 10.2439 4.63999 10.2439 4.88765 9.99537L7.42423 7.44993C7.54317 7.33058 7.60997 7.16873 7.60997 6.99996V3.1818C7.60997 2.83035 7.32604 2.54544 6.97583 2.54544C6.62559 2.54544 6.34168 2.83035 6.34168 3.1818V6.73638L3.99084 9.09543Z"
                                fill="#9F9FA9"
                              />
                            </svg>
                          </div> */}
                        </div>
                      </div>

                      {/* Performance */}
                      <div className="text-right flex items-center gap-1">
                        <p className="text-violet-300 font-bold text-lg opacity-50">
                          {token.lastPower}
                        </p>
                        <svg
                          className="w-3 h-3"
                          viewBox="0 0 17 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0 6H8V0H11V3H14V6H17V11H14V14H11V17H8V11H0V6Z"
                            fill="white"
                          />
                        </svg>
                        <p className="text-violet-300 font-bold text-lg -mt-1">
                          {token.currentPower}{" "}
                          <span className="text-xs align-top">
                            (
                            {token.currentPower - token.lastPower >= 0
                              ? "+"
                              : ""}
                            {token.currentPower - token.lastPower})
                          </span>
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-400">
                    <p>No tokens found for {selectedFilter} position</p>
                    <p className="text-sm mt-1">
                      Try selecting a different filter
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Filter and List Section */}
    </div>
  );
}
